import { type ChangeEvent, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { formatNumber, parseMoney } from "@/lib/formatters";

interface MoneyInputProps {
  value: number | null | undefined;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  "aria-label"?: string;
}

/**
 * Input para montos en pesos colombianos. Formatea en vivo a "1.234.567"
 * y devuelve el número crudo al onChange.
 */
export function MoneyInput({
  value,
  onChange,
  placeholder = "$0",
  className,
  disabled,
  id,
  name,
  "aria-label": ariaLabel,
}: MoneyInputProps) {
  const [display, setDisplay] = useState<string>(value ? formatNumber(value) : "");

  useEffect(() => {
    setDisplay(value ? formatNumber(value) : "");
  }, [value]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const raw = parseMoney(e.target.value);
    setDisplay(raw ? formatNumber(raw) : "");
    onChange(raw);
  }

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted">
        $
      </span>
      <input
        type="text"
        inputMode="numeric"
        id={id}
        name={name}
        aria-label={ariaLabel}
        value={display}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "money w-full rounded-md border border-border bg-bg-card px-3 py-2.5 pl-7 text-right",
          "focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          className,
        )}
      />
    </div>
  );
}
